import { Injectable } from "@angular/core";
import { IndexedDBService } from "../indexed-db/indexed-db.service";
import { Observable, switchMap, Subject, catchError } from 'rxjs';
import { UploadJob } from "../indexed-db/indexed-db.interface";
import { AssetService } from "@dal/core";
import { JobStatus } from '@dal/async-upload';
import {
  AsyncUploadClient,
  UploadProgress,
  UploadResult,
  WorkerMessage,
  WorkerMessageType,
  ProgressMessage,
  CompletionMessage,
  ErrorMessage,
} from '@dal/async-upload';

@Injectable({ providedIn: 'root' })
export class UploadWorkerService {
  private uploadClient: AsyncUploadClient;
  private progressSubject = new Subject<UploadProgress>();
  private completionSubject = new Subject<UploadResult[]>();
  private errorSubject = new Subject<string>();
  private jobCompletionSubject = new Subject<string>(); // New subject for job completion
  private completedJobs = new Set<string>(); // Track completed jobs to prevent duplicates

  public progress$ = this.progressSubject.asObservable();
  public completion$ = this.completionSubject.asObservable();
  public error$ = this.errorSubject.asObservable();
  public jobCompletion$ = this.jobCompletionSubject.asObservable(); // Observable for job completion

  constructor(
    private indexedDbService: IndexedDBService,
    private assetService: AssetService
  ) {
    console.log('🔧 UploadWorkerService constructor called');
    // Don't initialize in constructor - wait for explicit initialization
    // this.initializeUploadClient().catch(error => {
    //   console.error('❌ Failed to initialize upload client in constructor:', error);
    // });

    // Attach debugging helpers to global scope
    this.attachGlobalDebugger();
  }

  private async initializeUploadClient(): Promise<void> {
    try {
      console.log('🏗️ Creating new AsyncUploadClient...');
      this.uploadClient = new AsyncUploadClient({
        parallelUploads: 3,
        retries: {
          maxRetries: 3,
          initialDelayMs: 1000,
          maxDelayMs: 10000,
          backoffMultiplier: 2,
        },
      });

      console.log('📂 Checking worker file accessibility...');
      console.log('Worker path: /assets/workers/async-upload.worker.min.js');

      // Check if worker file exists
      try {
        const response = await fetch(
          '/assets/workers/async-upload.worker.min.js'
        );
        console.log('📥 Worker file fetch response:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          url: response.url,
        });
        if (!response.ok) {
          throw new Error(
            `Worker file not found: ${response.status} ${response.statusText}`
          );
        }
        const workerContent = await response.text();
        console.log('📄 Worker file size:', workerContent.length, 'bytes');
        console.log(
          '📄 Worker file first 100 chars:',
          workerContent.substring(0, 100)
        );
      } catch (fetchError) {
        console.error('❌ Failed to fetch worker file:', fetchError);
        throw fetchError;
      }

      console.log('🚀 Initializing upload client with worker...');
      await this.uploadClient.initialize(
        '/assets/workers/async-upload.worker.min.js'
      );
      console.log('✅ Upload client initialized successfully');
      console.log(
        '🔍 Upload client ready status:',
        this.uploadClient.isReady()
      );

      // Set up event handlers
      console.log('📡 Setting up message subscription...');
      this.uploadClient.getAllMessages().subscribe({
        next: (message: WorkerMessage) => {
          console.log('📨 Raw worker message received:', message);
          this.handleWorkerMessage(message);
        },
        error: (error) => {
          console.error('❌ Error receiving worker messages:', error);
        },
        complete: () => {
          console.log('🔚 Worker message stream completed');
        },
      });

      console.log('✅ Worker message subscription established');
    } catch (error) {
      console.error('❌ Failed to initialize upload client:', error);
      throw error;
    }
  }

  private handleWorkerMessage(message: WorkerMessage): void {
    console.log(
      'Received worker message:',
      message.type,
      'for job:',
      message.jobId
    );

    switch (message.type) {
      case WorkerMessageType.UPLOAD_PROGRESS: {
        const progressMsg = message as ProgressMessage;
        console.log('Progress update:', progressMsg.progress);
        this.progressSubject.next(progressMsg.progress);
        this.updateJobProgress(progressMsg.progress);
        break;
      }

      case WorkerMessageType.UPLOAD_COMPLETE: {
        const completionMsg = message as CompletionMessage;
        console.log('Upload complete message received:', completionMsg);
        this.completionSubject.next(completionMsg.results);
        this.handleUploadCompletion(message.jobId, completionMsg.results);
        break;
      }

      case WorkerMessageType.UPLOAD_FAILED:
      case WorkerMessageType.JOB_ERROR: {
        const errorMsg = message as ErrorMessage;
        console.error('Upload error message:', errorMsg);
        this.errorSubject.next(errorMsg.error);
        this.handleUploadError(message.jobId, errorMsg.error);
        break;
      }

      case WorkerMessageType.JOB_STARTED:
        this.handleJobStatusMessage(
          message.jobId,
          'Job started',
          JobStatus.UPLOADING
        );
        break;

      case WorkerMessageType.JOB_PAUSED:
        this.handleJobStatusMessage(
          message.jobId,
          'Job paused',
          JobStatus.PAUSED,
          (job) => job.status !== JobStatus.PAUSED
        );
        break;

      case WorkerMessageType.JOB_RESUMED:
        this.handleJobStatusMessage(
          message.jobId,
          'Job resumed',
          JobStatus.UPLOADING,
          (job) => job.status === JobStatus.PAUSED
        );
        break;

      case WorkerMessageType.JOB_CANCELED:
        this.handleJobStatusMessage(
          message.jobId,
          'Job canceled',
          JobStatus.CANCELED,
          (job) =>
            job.status !== JobStatus.CANCELED &&
            job.status !== JobStatus.COMPLETE
        );
        break;

      default:
        console.warn('Unknown worker message type:', message.type);
    }
  }

  /**
   * Start uploading files for a job
   */
  async startUpload(
    jobId: string,
    files: File[],
    presignedUrls: string[]
  ): Promise<void> {
    try {
      console.log(
        `🚀 Starting upload for job ${jobId} with ${files.length} files`
      );
      console.log('📋 Presigned URLs:', presignedUrls);
      console.log(
        '📁 Files:',
        files.map((f) => ({ name: f.name, size: f.size, type: f.type }))
      );

      // Always initialize the client fresh to ensure it's ready
      console.log('🔄 Initializing upload client...');
      await this.initializeUploadClient();

      // Wait a bit for the worker to be fully ready
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (!this.uploadClient?.isReady()) {
        throw new Error('Upload client failed to initialize properly');
      }

      console.log('✅ Upload client ready, starting upload...');
      console.log('🔍 Upload client status:', {
        isReady: this.uploadClient.isReady(),
        jobId,
        filesCount: files.length,
        urlsCount: presignedUrls.length,
      });

      // Start the upload and log the observable
      const uploadObservable = this.uploadClient.startUpload(
        jobId,
        files,
        presignedUrls
      );
      console.log('📡 Upload observable created:', uploadObservable);

      uploadObservable.subscribe({
        next: (message) => {
          console.log(
            '📨 Upload stream message received:',
            message.type,
            message
          );
        },
        error: (error) => {
          console.error('❌ Upload stream error:', error);
          this.handleUploadError(jobId, error.message || 'Upload stream error');
        },
        complete: () => {
          console.log('✅ Upload stream completed for job:', jobId);
        },
      });

      console.log('📡 Upload stream subscription established');
    } catch (error) {
      console.error('❌ Failed to start upload:', error);
      throw error;
    }
  }

  cancelJob(jobID: string): Observable<UploadJob | null> {
    return this.performJobControlOperation(
      jobID,
      (id) => this.indexedDbService.cancelJob(id),
      (id) => this.uploadClient.cancelJob(id)
    );
  }

  pauseJob(jobID: string): Observable<UploadJob | null> {
    return this.performJobControlOperation(
      jobID,
      (id) => this.indexedDbService.pauseJob(id),
      (id) => this.uploadClient.pauseJob(id)
    );
  }

  resumeJob(jobID: string): Observable<UploadJob | null> {
    return this.performJobControlOperation(
      jobID,
      (id) => this.indexedDbService.resumeJob(id),
      (id) => this.uploadClient.resumeJob(id)
    );
  }

  /**
   * Creates assets from job data after files have been uploaded to S3
   * @param jobID ID of the upload job
   * @returns Observable of the updated job
   */
  createAssetsFromJob(jobID: string): Observable<UploadJob> {
    return this.indexedDbService.get(jobID).pipe(
      switchMap((job: UploadJob) => {
        if (!job) {
          throw new Error(`Job ${jobID} not found`);
        }

        return this.assetService.createAssetBulk(job.data).pipe(
          switchMap((assets) => {
            console.log('Assets created successfully:', assets);
            job.status = JobStatus.COMPLETE;
            job.completion = 100;
            job.lastModified = Date.now();
            return this.indexedDbService.update(job);
          })
        );
      })
    );
  }

  /**
   * Helper method to update job status
   * @param jobID ID of the job to update
   * @param status New status to set
   * @param additionalUpdates Optional function to apply additional updates to the job
   * @returns Observable of the updated job
   */
  private updateJobStatus(
    jobID: string,
    status: JobStatus,
    additionalUpdates?: (job: UploadJob) => void
  ): Observable<UploadJob | null> {
    return this.indexedDbService.get(jobID).pipe(
      switchMap((job) => {
        if (job) {
          job.status = status;
          job.lastModified = Date.now();

          if (additionalUpdates) {
            additionalUpdates(job);
          }

          return this.indexedDbService.update(job);
        }
        return new Observable<null>((observer) => {
          observer.next(null);
          observer.complete();
        });
      })
    );
  }

  /**
   * Helper method to update job status conditionally
   * @param jobID ID of the job to update
   * @param status New status to set
   * @param condition Function to determine if the job should be updated
   * @param additionalUpdates Optional function to apply additional updates to the job
   * @returns Observable of the updated job
   */
  private updateJobStatusConditionally(
    jobID: string,
    status: JobStatus,
    condition: (job: UploadJob) => boolean,
    additionalUpdates?: (job: UploadJob) => void
  ): Observable<UploadJob | null> {
    return this.indexedDbService.get(jobID).pipe(
      switchMap((job) => {
        if (job && condition(job)) {
          return this.updateJobStatus(jobID, status, additionalUpdates);
        }
        return new Observable<null>((observer) => {
          observer.next(null);
          observer.complete();
        });
      })
    );
  }

  /**
   * Update job progress based on upload progress
   */
  private updateJobProgress(progress: UploadProgress): void {
    const jobId = progress.jobId;
    if (jobId) {
      this.indexedDbService.get(jobId).subscribe((job) => {
        if (job) {
          job.completion = progress.overallProgress;
          job.lastModified = Date.now();
          this.indexedDbService.update(job).subscribe();
        }
      });
    }
  }

  /**
   * Handle upload completion - prevents duplicate asset creation
   */
  private handleUploadCompletion(jobId: string, results: UploadResult[]): void {
    console.log(
      'Upload completion received for job:',
      jobId,
      'Results:',
      results
    );

    if (!jobId) {
      console.warn('Upload completion received but no jobId provided');
      return;
    }

    // Check if this job has already been processed
    if (this.completedJobs.has(jobId)) {
      console.log(
        '📋 Job already processed, skipping duplicate completion:',
        jobId
      );
      return;
    }

    // Mark job as being processed to prevent duplicates
    this.completedJobs.add(jobId);

    // Get the job and check its current status
    this.indexedDbService.get(jobId).subscribe({
      next: (job) => {
        if (!job) {
          console.error('Job not found:', jobId);
          this.completedJobs.delete(jobId); // Remove from tracking since job doesn't exist
          return;
        }

        // Check if assets have already been created (job is complete)
        if (job.status === JobStatus.COMPLETE) {
          console.log(
            '📋 Job already completed, skipping asset creation:',
            jobId
          );
          // Still emit completion event for UI updates but don't create assets again
          this.jobCompletionSubject.next(jobId);
          return;
        }

        // Mark job as complete first to prevent duplicate processing
        job.status = JobStatus.COMPLETE;
        job.completion = 100;
        job.lastModified = Date.now();

        this.indexedDbService.update(job).subscribe({
          next: () => {
            console.log('🏗️ Creating assets for job:', jobId);
            // Now create assets
            this.createAssetsFromJob(jobId).subscribe({
              next: (finalJob) => {
                console.log(
                  '✅ Upload completed and assets created for job:',
                  jobId,
                  finalJob
                );
                // Emit job completion event
                this.jobCompletionSubject.next(jobId);
              },
              error: (error) => {
                console.error(
                  '❌ Failed to create assets for job:',
                  jobId,
                  error
                );
                // Revert job status if asset creation fails
                job.status = JobStatus.FAILED;
                this.indexedDbService.update(job).subscribe();
                // Remove from completed jobs so it can be retried
                this.completedJobs.delete(jobId);
              },
            });
          },
          error: (error) => {
            console.error(
              '❌ Failed to update job status for completion:',
              jobId,
              error
            );
            // Remove from completed jobs so it can be retried
            this.completedJobs.delete(jobId);
          },
        });
      },
      error: (error) => {
        console.error('Failed to get job for completion check:', jobId, error);
        // Remove from completed jobs so it can be retried
        this.completedJobs.delete(jobId);
      },
    });
  }

  /**
   * Handle upload errors
   */
  private handleUploadError(jobId: string, error: string): void {
    console.error('Upload error:', error);
    this.updateJobStatusSafely(
      jobId,
      JobStatus.FAILED,
      'Upload error occurred'
    );
  }

  /**
   * Initialize and start listening to worker messages
   * This method provides compatibility with the existing app component
   */
  async listenToWorker(): Promise<void> {
    if (!this.uploadClient?.isReady()) {
      await this.initializeUploadClient();
    }
    // Worker is already listening through the message subscription
    console.log('Upload worker service is ready and listening');
  }

  /**
   * Get the current status of the upload client for debugging
   */
  getClientStatus(): { isReady: boolean; hasWorker: boolean } {
    try {
      return {
        isReady: this.uploadClient?.isReady() || false,
        hasWorker:
          this.uploadClient && typeof this.uploadClient.getWorker === 'function'
            ? this.uploadClient.getWorker() !== null
            : false,
      };
    } catch (error) {
      console.error('Error getting client status:', error);
      return {
        isReady: false,
        hasWorker: false,
      };
    }
  }

  /**
   * Manual trigger for testing completion flow
   */
  testCompletion(jobId: string): void {
    console.log('🧪 Testing completion flow for job:', jobId);
    this.handleUploadCompletion(jobId, [
      {
        fileName: 'test.txt',
        success: true,
        retries: 0,
        uploadTime: 1000,
        size: 1024,
      },
    ]);
  }

  /**
   * Manual trigger to test if upload completion works
   * This helps debug if the issue is in worker communication or completion handling
   */
  async debugTestUploadCompletion(jobId: string): Promise<void> {
    console.log('🧪 Debug: Testing upload completion for job:', jobId);

    try {
      // Simulate upload completion
      const mockResults: UploadResult[] = [
        {
          fileName: 'test.jpg',
          success: true,
          retries: 0,
          uploadTime: 1000,
          size: 1024,
        },
      ];

      console.log('🧪 Debug: Calling handleUploadCompletion...');
      this.handleUploadCompletion(jobId, mockResults);

      console.log('🧪 Debug: Upload completion test initiated');
    } catch (error) {
      console.error('🧪 Debug: Error in upload completion test:', error);
    }
  }

  /**
   * Global debugging helper - attach to window for console access
   * Call from browser console: (window as any).uploadWorkerDebug.testCompletion('your-job-id')
   */
  attachGlobalDebugger(): void {
    if (typeof window !== 'undefined') {
      (window as { uploadWorkerDebug?: unknown }).uploadWorkerDebug = {
        testCompletion: (jobId: string) =>
          this.debugTestUploadCompletion(jobId),
        checkStatus: () => this.getClientStatus(),
        getJobs: () => this.checkPendingJobs(),
        forceInit: () => this.initializeUploadClient(),
      };
      console.log(
        '🧪 Debug: Upload worker debug helpers attached to window.uploadWorkerDebug'
      );
    }
  }

  /**
   * Check pending jobs and their status
   */
  async checkPendingJobs(): Promise<void> {
    console.log('🔍 Checking pending jobs...');
    // You can call this method from browser console to debug pending jobs
  }

  /**
   * Common pattern for job control operations
   */
  private performJobControlOperation(
    jobID: string,
    dbOperation: (id: string) => Observable<UploadJob | null>,
    clientOperation: (id: string) => void
  ): Observable<UploadJob | null> {
    return dbOperation(jobID).pipe(
      switchMap(() => {
        // Perform client operation if ready
        if (this.uploadClient?.isReady()) {
          clientOperation(jobID);
        }
        return this.indexedDbService.get(jobID);
      }),
      catchError((error) => {
        console.error(`Error in job control operation for ${jobID}:`, error);
        return new Observable<null>((observer) => {
          observer.next(null);
          observer.complete();
        });
      })
    );
  }

  /**
   * Common job status update handler
   */
  private handleJobStatusMessage(
    jobId: string,
    messageType: string,
    newStatus: JobStatus,
    condition?: (job: UploadJob) => boolean
  ): void {
    console.log(`${messageType}:`, jobId);
    const defaultCondition = () => true;
    this.updateJobStatusConditionally(
      jobId,
      newStatus,
      condition || defaultCondition
    ).subscribe();
  }

  /**
   * Common method for updating job status with error handling
   */
  private updateJobStatusSafely(
    jobId: string,
    status: JobStatus,
    errorContext?: string
  ): void {
    if (!jobId) {
      console.warn(
        `Cannot update job status: ${errorContext || 'No jobId provided'}`
      );
      return;
    }

    this.indexedDbService.get(jobId).subscribe({
      next: (job) => {
        if (job) {
          job.status = status;
          job.lastModified = Date.now();
          this.indexedDbService.update(job).subscribe();
        } else {
          console.warn(
            `Job ${jobId} not found when trying to update status to ${status}`
          );
        }
      },
      error: (error) => {
        console.error(
          `Failed to update job ${jobId} status to ${status}:`,
          error
        );
      },
    });
  }
}
